import { useEffect } from "react";
import { Alert } from "antd";
import { TFormHookParams, IFormSection } from "../../types/common";
import useGet from "../common/useGet";
import { API_DISTRICTS, API_PAY_WEEK_CONFIGURATION } from "../../utils/apis";
import { getRegister } from "../../utils/helpers";
import { URL_PAY_WEEK_CONFIGURATION } from "../../utils/urls";
import { weekdayName, weekdayOptions } from "./usePayWeek";
import { isDay, payWeekLength, spansSunday } from "../../utils/constants";

export const usePayWeekConfigurationForm = ({
  id,
  updateFormValues,
  form,
}: TFormHookParams = {}) => {
  const isUpdate = !!id;
  const hasSpan = isDay(form?.start_day) && isDay(form?.end_day);
  const sundayExcluded =
    hasSpan &&
    !!form?.exclude_sunday &&
    spansSunday(form.start_day, form.end_day);
  const spanDays = hasSpan
    ? payWeekLength(form.start_day, form.end_day) - (sundayExcluded ? 1 : 0)
    : 0;
  const {
    loadData: getDistricts,
    data: districts,
    loading: loadingDistricts,
  } = useGet();

  useEffect(() => {
    if (isUpdate && form) {
      updateFormValues?.({
        ...form,
        district: form.district?.id || form.district,
      });
    }
  }, [form, isUpdate, updateFormValues]);

  useEffect(() => {
    getDistricts({ api: API_DISTRICTS });
  }, []);

  const sections: IFormSection[] = [
    {
      title: "Pay Week Details",
      fields: [
        {
          field_name: "district",
          type: "select",
          label: "City",
          props: {
            required: !isUpdate,
            placeholder: "Select City",
          },
          options_data: districts,
          value: form?.district,
          loading: loadingDistricts,
        },
        {
          field_name: "start_day",
          label: "Start Day",
          type: "select",
          props: {
            required: !isUpdate,
            placeholder: "Select start day",
            options: weekdayOptions,
          },
        },
        {
          field_name: "end_day",
          label: "End Day",
          type: "select",
          props: {
            required: !isUpdate,
            placeholder: "Select end day",
            options: weekdayOptions,
          },
        },
        {
          field_name: "exclude_sunday",
          label: "Exclude Sunday",
          type: "checkbox",
          props: { required: false },
        },
        {
          field_name: "pay_week_span",
          type: "component",
          visible: hasSpan,
          props: { className: "fullWidth" },
          component: (
            <Alert
              showIcon
              type="info"
              message={
                <>
                  {weekdayName(form?.start_day)} to {weekdayName(form?.end_day)}{" "}
                  is{" "}
                  <b>
                    {spanDays} {spanDays === 1 ? "day" : "days"}
                  </b>
                  {sundayExcluded && " (Sunday excluded)"}
                </>
              }
            />
          ),
        },
        {
          field_name: "is_active",
          label: "Status",
          type: "select",
          visible: isUpdate,
          props: {
            placeholder: "Select Status",
            options: [
              { value: true, label: "Active" },
              { value: false, label: "Inactive" },
            ],
          },
        },
      ],
    },
  ];

  const title = "Pay Week";
  const redirect = getRegister(URL_PAY_WEEK_CONFIGURATION);

  const onSubmit = (payload: any) => {
    if (id) {
      return {
        id,
        api: API_PAY_WEEK_CONFIGURATION,
        params: {
          ...payload,
        },
      };
    }
    return {
      api: API_PAY_WEEK_CONFIGURATION,
      params: {
        ...payload,
      },
    };
  };

  return {
    sections,
    onSubmit,
    title,
    redirect,
  };
};
